import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, Circle } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDateTime } from "@/shared/lib/date-time";
import { QueryState } from "@/shared/ui/QueryState";
import { useChecklistExecutionDetailQuery } from "../api/queries";

const statusLabels: Record<"in_progress" | "completed" | "cancelled", string> =
  {
    in_progress: "ກຳລັງດຳເນີນການ",
    completed: "ສຳເລັດ",
    cancelled: "ຍົກເລີກ",
  };

const statusVariant: Record<
  "in_progress" | "completed" | "cancelled",
  "default" | "secondary" | "outline" | "destructive"
> = {
  in_progress: "secondary",
  completed: "default",
  cancelled: "destructive",
};

export function ChecklistExecutionDetailPage() {
  const nav = useNavigate();
  const { executionId } = useParams({
    from: "/app/checklists/execution-history/$executionId",
  });

  const detail = useChecklistExecutionDetailQuery(executionId ?? "");
  const data = detail.data?.data;

  return (
    <>
      <Header />
      <Main>
        <QueryState
          result={detail}
          variant="fullscreen"
          isEmpty={!data}
          emptyFallback={
            <div className="text-center">
              <p className="text-muted-foreground">ບໍ່ພົບການບັນທຶກນີ້</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => nav({ to: "/app/checklists/execution-history" })}
              >
                ກັບປະຫວັດການບັນທຶກ
              </Button>
            </div>
          }
        >
          {data && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="">
                  <h1 className="font-bold text-2xl tracking-tight">
                    ລາຍລະອຽດການບັນທຶກ Checklist
                  </h1>
                  <p className="mt-1 text-muted-foreground">
                    {data.checklistTitle}
                  </p>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    nav({ to: "/app/checklists/execution-history" })
                  }
                  className="mb-2 -ml-2"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  ກັບປະຫວັດການບັນທຶກ
                </Button>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">ຂໍ້ມູນທົ່ວໄປ</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <div className="text-muted-foreground">ສະຖານະ</div>
                    <Badge variant={statusVariant[data.status]}>
                      {statusLabels[data.status]}
                    </Badge>
                  </div>
                  <div>
                    <div className="text-muted-foreground">ພະແນກ</div>
                    <div>{data.departmentName ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">ພະນັກງານ</div>
                    <div>{data.userName}</div>
                    <div className="text-muted-foreground text-xs">
                      {data.userEmail}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">ເວລາເລີ່ມ</div>
                    <div>{formatDateTime(data.startedAt)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">ເວລາສຳເລັດ</div>
                    <div>
                      {data.completedAt
                        ? formatDateTime(data.completedAt)
                        : "—"}
                    </div>
                  </div>
                  {data.notes ? (
                    <div className="sm:col-span-2">
                      <div className="text-muted-foreground">ໝາຍເຫດລວມ</div>
                      <p className="mt-1 whitespace-pre-wrap">{data.notes}</p>
                    </div>
                  ) : null}
                  <div className="text-muted-foreground text-xs sm:col-span-2">
                    ອັບເດດຫຼ້າສຸດ: {formatDateTime(data.updatedAt)}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">ລາຍການຂໍ້ທີ່ບັນທຶກ</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {data.items.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      ບໍ່ມີລາຍການຂໍ້ (ຫຼືຍັງບໍ່ມີການບັນທຶກຍ່ອຍ)
                    </p>
                  ) : (
                    <ul className="space-y-4">
                      {data.items.map((item) => (
                        <li
                          key={item.id}
                          className="border-b pb-4 last:border-0 last:pb-0"
                        >
                          <div className="flex gap-3">
                            <div className="mt-0.5 shrink-0">
                              {item.isCompleted ? (
                                <CheckCircle2 className="h-5 w-5 text-primary" />
                              ) : (
                                <Circle className="h-5 w-5 text-muted-foreground" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1 space-y-1">
                              <div className="font-medium">
                                {item.order}. {item.title}
                              </div>
                              {item.description ? (
                                <p className="text-muted-foreground text-sm">
                                  {item.description}
                                </p>
                              ) : null}
                              {item.formName ? (
                                <p className="text-muted-foreground text-xs">
                                  ຟອມທີ່ຜູກ: {item.formName}
                                </p>
                              ) : null}
                              {item.notes ? (
                                <p className="mt-2 whitespace-pre-wrap text-sm">
                                  <span className="text-muted-foreground">
                                    ໝາຍເຫດ:{" "}
                                  </span>
                                  {item.notes}
                                </p>
                              ) : null}
                              {item.photoUrl ? (
                                <div className="mt-2">
                                  <a
                                    href={item.photoUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary text-sm underline"
                                  >
                                    ເບິ່ງຮູບພາບ
                                  </a>
                                </div>
                              ) : null}
                              {item.completedAt ? (
                                <p className="text-muted-foreground text-xs">
                                  ບັນທຶກ: {formatDateTime(item.completedAt)}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() =>
                    nav({
                      to: "/app/checklists/$id",
                      params: { id: data.checklistId },
                    })
                  }
                >
                  ເປີດແບບຟອມ Checklist
                </Button>
              </div>
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
